import type { OrderedSearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { filtersConfigStatusMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";

import { createAndroidFilterConfig } from "../configs/createAndroidFilterConfig";
import { createIosFilterConfig } from "../configs/createIosFilterConfig";
import type { AppOperatingSystem } from "../types/commonFiltersDto";
import type { IFindAppsDescriptionDto } from "../types/descriptionDto";

const getConfigFactory = (
    appOs: AppOperatingSystem,
): ((description?: IFindAppsDescriptionDto) => FiltersConfig<OrderedSearchFilterInstance[]>) | undefined => {
    switch (appOs) {
        case "android":
            return createAndroidFilterConfig;
        case "ios":
            return createIosFilterConfig;
        default:
            return undefined;
    }
};

/**
 * @throws Error
 */
export const composeAppsFiltersLockedConfig = (
    platformFilter: OrderedSearchFilterInstance,
    appOs: AppOperatingSystem | undefined,
    description: IFindAppsDescriptionDto,
) => {
    const platform: AppOperatingSystem = appOs ?? platformFilter.filter.getInitialDtoValue();

    const lockedConfig = getConfigFactory(platform)?.(description);

    if (!lockedConfig) {
        throw new Error(`[composeAppsFiltersConfig] Unable to create locked config for ${platform}`);
    }

    return {
        filtersList: [platformFilter, ...lockedConfig.filtersList],
        status: filtersConfigStatusMap.ready,
    };
};

/**
 * @throws Error
 */
export const composeAppsFiltersConfig = (
    platformFilter: OrderedSearchFilterInstance,
    appOs: AppOperatingSystem | undefined,
    description: IFindAppsDescriptionDto | undefined,
): FiltersConfig<OrderedSearchFilterInstance[]> => {
    const platform: AppOperatingSystem = appOs ?? platformFilter.filter.getInitialDtoValue();

    if (!description) {
        return {
            filtersList: [platformFilter],
            status: filtersConfigStatusMap.initial,
        };
    }

    const config = getConfigFactory(platform)?.(description);

    if (!config) {
        throw new Error(`[composeAppsFiltersConfig] Unable to create config for ${platform}`);
    }

    return {
        filtersList: [platformFilter, ...config.filtersList],
        status: config.status,
    };
};
